import { type MultiWatchSources, onWatcherCleanup, ref, shallowRef, watch, type WatchSource } from "vue";

type SetState<T> = (state: T | ((currentState: T) => T)) => void;

/** Creates a reactive async state handler with watching. */
export const asyncState = <T>(
  promise: (setState: SetState<T>) => Promise<T> | { response: Promise<T>; cancel: () => void },
  options: {
    /* The initial state of the reactive state */
    default: T;
    /* The source to watch for re-execution */
    watch: WatchSource | MultiWatchSources;
    /* Whether to keep the old state when re-executing */
    preserveState?: boolean;
    /* Callback when error is caught */
    onError?: (error: unknown) => void;
    /* Do not execute the promise immediately */
    noInitialExecution?: boolean;
  }
) => {
  const state = shallowRef<T>(options.default);
  const isLoading = ref<boolean>(false);

  let latestExecution = 0;
  const execute = async () => {
    const execution = (latestExecution += 1);
    const setState: SetState<T> = (newState) => {
      if (execution === latestExecution) {
        state.value = typeof newState === "function" ? (newState as (currentState: T) => T)(state.value) : newState;
      }
    };

    const resetState = () => {
      if (!options.preserveState) {
        setState(options.default);
      }
    };

    const result = promise(setState);

    let executePromise: Promise<T>;
    if ("cancel" in result) {
      onWatcherCleanup(result.cancel);
      executePromise = result.response;
    } else {
      executePromise = result;
    }

    // Set loading state if promise is actually async
    // oxlint-disable-next-line dart/no-set-timeout
    const timeout = setTimeout(() => {
      if (execution === latestExecution) {
        resetState();
        isLoading.value = true;
      }
    });

    // If there's no value, we want to show loading state immediately
    if (options.default === undefined && state.value === undefined) {
      isLoading.value = true;
    }

    try {
      setState(await executePromise);
    } catch (error) {
      if (execution === latestExecution) {
        resetState();
        if (options.onError) {
          options.onError(error);
        }
      }
    } finally {
      clearTimeout(timeout);
      if (execution === latestExecution) {
        isLoading.value = false;
      }
    }
  };

  // Watch the source for changes
  watch(options.watch, execute);

  // Execute the promise immediately if not disabled
  if (!options.noInitialExecution) {
    execute();
  }

  return { state, isLoading, execute };
};

/**
 * Filters an array using an async predicate function, executing in parallel while maintaining original order
 * @param array The input array to filter
 * @param predicate The async function to test each element
 * @returns A new filtered array with elements in their original order
 */
export const asyncFilter = async <T>(
  array: T[],
  predicate: (item: T, index: number) => Promise<boolean>
): Promise<T[]> => {
  const results = await Promise.all(
    array.map(async (item, index) => {
      const shouldKeep = await predicate(item, index);
      return [index, shouldKeep] as const;
    })
  );

  const indexToPass = new Map(results);
  return array.filter((_, index) => indexToPass.get(index));
};
